import React, { useContext, useRef, useState, useEffect } from 'react'
import { motion, useInView, useAnimationControls } from 'framer-motion/dist/framer-motion'
import parse, { domToReact } from 'html-react-parser'

import { DataContext } from '../../../Context/DataContext'

import './Contact.scss'

import Section from '../../Section'
import Social from '../../Social'
import Waves from './Waves'

const options = {
    replace: ({ attribs, children }) => {
        if (!attribs) return;

        const tagNames = ['h1', 'h2', 'h3', 'p', 'ul'];
        const tagName = attribs.tagName;

        if (tagNames.includes(tagName)) {
            if (tagName === 'ul') {
                return (
                    <ul>
                        {children.map((child, i) => 
                            <li key={`sub-item-${i}`}>
                                {domToReact(child.children, options)}
                            </li>
                        )}
                    </ul>
                );
            } else {
                return React.createElement(tagName, {}, domToReact(children, options));
            }
        }
    }
}

const children = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { duration: 0.5, type: 'tween' } }
}

const Contact = ({ data }) => {
    const { title, content } = data
    const { sectionsData } = useContext(DataContext)
    const [ paddingTop, setPaddingTop ] = useState(0)
    const [ margin, setMargin ] = useState(0)
    const svgRef = useRef(null)

    const controls = useAnimationControls()
    const gridContacts = useRef(null)
    const contactsVisible = useInView(gridContacts, { once: true, amount: 0.55 })

    const contacts = sectionsData?.find((item) => item.identifier === 'team-partners')

    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                duration: 0.5,
                staggerChildren: 0.5,
                type: 'tween',
                delayChildren: 1,
            }
        }
    }

    useEffect(() => {
        if (svgRef.current) {
            setPaddingTop(svgRef.current.clientHeight)
        }
    }, [svgRef])

    useEffect(() => {
        if (paddingTop) {
            setMargin(paddingTop / 2.5)
        }
    }, [paddingTop])

    useEffect(() => {
        if (contactsVisible) {
            controls.start("show")
        }
        return controls.stop
    }, [contactsVisible, controls])

    return (
        <>
            <Section
                name="mga-contact"
                className="mga-contact-section"
                motionProps={{
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    exit: { opacity: 0 },
                    transition: { duration: 0.5 }
                }}
            >
                <Waves ref={svgRef} />
                <div className="mga-section__wrapper"
                    style={{
                        paddingTop,
                    }}
                >
                    <motion.h1
                        className="mga-contact-section__title"
                        initial={{ opacity: 0, y: '100%' }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, type: 'tween', delay: 1.5 }}
                        style={{ marginTop: margin * -1, marginBottom: margin }}
                    >
                        { title }
                    </motion.h1>
                    <motion.div
                        className="mga-contact-section__top"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 2 }}
                    >
                        <div className="mga-contact-section__top__box mga-contact-section__top__box--left">
                            { parse(content.emails, options) }
                            <Social dark={true} />
                        </div>
                        <div className="mga-contact-section__top__box mga-contact-section__top__box--right">
                            { parse(content.address, options) }
                        </div>
                    </motion.div>
                    <motion.div
                        ref={gridContacts}
                        className="mga-contact-section__bottom"
                        initial="hidden"
                        variants={container}
                        animate={controls}
                    >
                        {contacts.content?.team?.map((item, index) => (
                            <motion.div
                                className="mga-contact-section__contact"
                                key={`mga-contact-${index}`}
                                variants={children}
                            >
                                <h3>
                                    {item.name}
                                    <span>{item.position}</span>
                                </h3>
                                {item.email && (
                                    <a href={`mailto:${encodeURIComponent(item.email)}`}>{item.email}</a>
                                )}
                                {item.phone && (
                                    <a href={`tel:+${item.phone.replace(/\D/g, '')}`}>
                                    {item.phone}
                                    </a>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </Section>
        </>
    )
}

export default React.memo(Contact)